import { Box, FormControlLabel, Grid, Radio, RadioGroup, SelectChangeEvent, Skeleton, Theme, Typography, useTheme } from "@mui/material"
import { useDispatch, useSelector } from "react-redux"
import { useHistory, useParams } from "react-router";
import { useRef, useState } from "react";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from 'yup';
import { RootStateType } from "../../../../redux-store/reducers";
import { isModuleAllowed, toINR } from "../../../../utils/utilityFunctions";
import BreadCrum from "../../../../lib/BreadCrum";
import { getFiscalYearOptions, getQuartersForFiscalYear } from "../../financialYear";
import MFSelectField from "../../../../lib/formik/SelectField";
import { MFSubmitButton } from "../../../../lib/formik";
import NoData from "../../NoData";
import { InfoIconOutlined } from "../../../../assets/SvgIcons";
import FamilyMemberDetailsTable from "./FamilyMemberDetailsTable";
import { nonNegativeNumberRegex } from "../../../../utils/regex";
import { getFamilyMembersFeeListData, updateRiaFee } from "../../../../redux-store/actions/reports";
import { FamilyMembersFeeListDataObj, GetFamilyMembersFeeListDataResponseBody, SlabDataObj, UpdateRiaFeeResponseBody } from "../../../../redux-store/types/onBoarding";
import { showError } from "../../../../redux-store/actions/auth";
import StatusPopup from "../../StatusPopup";

interface FilterValues {
    year: string;
    quarter: string;
}

const filterValidationSchema = Yup.object().shape({
    year: Yup
        .string()
        .required('Please select Fiscal Year'),
    quarter: Yup
        .string()
        .required('Please select Quarter'),
})

const initialFilterValues: FilterValues = {
    year: "",
    quarter: ""
}

const YEARS = getFiscalYearOptions()

export const FEE_TYPE_VALUES = {
    FIXED_FEE: "FIXED",
    AUA_FEE_VARIABLE: "VARIABLE",
    AUA_FEE_SLAB_WISE: "SLAB"
} as const

export type FeeTypeValue = typeof FEE_TYPE_VALUES[keyof typeof FEE_TYPE_VALUES]

const FEE_TYPES: Array<{ label: string; value: string }> = [
    { label: "Fixed fee", value: FEE_TYPE_VALUES.FIXED_FEE },
    { label: "AUA Fee (Variable)", value: FEE_TYPE_VALUES.AUA_FEE_VARIABLE },
    { label: "AUA Fee (Slab Wise)", value: FEE_TYPE_VALUES.AUA_FEE_SLAB_WISE },
]

const MAX_FIXED_FEE_AMOUNT = 151000
const MAX_FEE_PERCENT = 2.5
const MAX_DECIMAL_PLACES = 3

const tableFormValidationSchema = (locale: string, currencyCode: string) => {
    // Getting trimmed values
    const getTrimmedValue = (value: string) => `${value ?? ""}`.trim()

    // Checking if invoiceStatus is freezed or not
    const isFreezedStatus = (invoiceStatus: string) =>
        ["freezed", "freeze"].includes(getTrimmedValue(invoiceStatus).toLowerCase())

    // Checking if value is empty or not
    const isValueEmpty = (value: string) => !getTrimmedValue(value).length

    // Validate Row-level fields (fixed_rate, variable_rate)
    // Only validate if the row is editable (i.e. invoiceStatus != freezed) AND has a value
    // Why: freezed rows must not show format errors; empty values are validated by .required()
    const shouldValidateRowField = (value: string, invoiceStatus: string) =>
        !isFreezedStatus(invoiceStatus) && !isValueEmpty(value)


    // Validate Slab-level fields (slab_min, slab_max, rate)
    // No need to check for invoiceStatus === freezed as it is already taken care via feeDetails.when('invoiceStatus')
    // Only validate if the field has a value
    // Why: empty values are validated by .required()
    const shouldValidateSlabField = (value: string) => !isValueEmpty(value)

    // Accepts => integers and full decimals e.g. 0.45
    // Rejects => "", ".", ".45", "23.", and non-numeric input
    const isValidDecimalFormat = (value: string) => {
        if (!value || value === "." || value.endsWith(".")) {
            return false
        }

        // nonNegativeNumberRegex => checks whether the value is a non-negative integer or decimal number
        return (!Number.isNaN(Number(value)) && nonNegativeNumberRegex.test(value))
    }

    // Get number of digits after decimal point
    const getDecimalPlaces = (value: string) => `${value ?? ""}`.split(".")[1]?.length ?? 0

    // Reusable Yup test wrapper: run format/range validations only when shouldValidateRowField allows
    const rowFieldTest = (
        name: string,
        message: string,
        validate: (value: string) => boolean
    ) =>
        Yup.string().test(name, message, function (value) {
            // Row should not be validated when invoiceStatus is freezed OR value is empty (there is seperate validation for empty value; no need to check format/range validation on empty value field)
            if (!shouldValidateRowField((value ?? ""), this.parent?.invoiceStatus)) {
                return true
            }

            // Row should be validated when invoiceStatus is NOT freezed AND value is NOT empty
            return validate(getTrimmedValue(value ?? ""))
        })

    // Reusable Yup test wrapper: run format/range validations only when shouldValidateSlabField allows
    const slabFieldTest = (
        name: string,
        message: string,
        validate: (value: string) => boolean
    ) =>
        Yup.string().test(name, message, function (value) {
            // Slab should not be validated when value is empty (there is seperate validation for empty value; no need to check format/range validation on empty value field)
            if (!shouldValidateSlabField(value ?? "")) {
                return true
            }
            // Slab should be validated when value is NOT empty
            return validate(getTrimmedValue(value ?? ""))
        })

    // Validation for required field
    const requiredIfNotFreezed = (message: string) =>
        Yup.string().test("required-if-not-freezed", message, function (value) {
            if (isFreezedStatus(this.parent?.invoiceStatus)) {
                return true
            }

            return !isValueEmpty(value ?? "")
        })

    const stripCommas = (value: string) => `${value ?? ""}`.replaceAll(',', '')

    // Field validators: transform(if applicable) → required → format → business limits (concat order matters)
    const fixedFeeAmountValidator = Yup.string()
        .transform((value) => stripCommas(value))
        .concat(requiredIfNotFreezed("Annual Fixed Fee Amount is required"))
        .concat(rowFieldTest("valid-decimal-format", "Please enter a valid number", isValidDecimalFormat))
        .concat(
            Yup.string().test("max-fixed-fee", `Maximum value allowed is ${toINR({ value: MAX_FIXED_FEE_AMOUNT, locale, currencyCode })}`, function (value) {
                if (!shouldValidateRowField((value ?? ""), this.parent?.invoiceStatus)) {
                    return true
                }

                // Skip max limit for non-individual investors
                if (`${this.parent?.status ?? ""}`.toLowerCase() === "non-individual") {
                    return true
                }

                return Number(getTrimmedValue(value ?? "")) <= MAX_FIXED_FEE_AMOUNT
            })
        )

    const feePercentValidator = requiredIfNotFreezed("Fee percentage P.A is required")
        .concat(rowFieldTest("valid-decimal-format", "Please enter a valid number", isValidDecimalFormat))
        .concat(rowFieldTest("max-fee-percent", `Maximum value allowed is ${MAX_FEE_PERCENT}`, (v) => Number(v) <= MAX_FEE_PERCENT))
        .concat(rowFieldTest("fee-percent-precision", `Maximum ${MAX_DECIMAL_PLACES} decimal places are allowed`, (v) => getDecimalPlaces(v) <= MAX_DECIMAL_PLACES))

    const slabFeePercentValidator = Yup.string()
        .required("Fee percentage P.A is required")
        .concat(slabFieldTest("valid-decimal-format", "Please enter a valid number", isValidDecimalFormat))
        .concat(slabFieldTest("max-fee-percent", `Maximum value allowed is ${MAX_FEE_PERCENT}`, (v) => Number(v) <= MAX_FEE_PERCENT))
        .concat(slabFieldTest("fee-percent-precision", `Maximum ${MAX_DECIMAL_PLACES} decimal places are allowed`, (v) => getDecimalPlaces(v) <= MAX_DECIMAL_PLACES))

    const slabAumValidator = (requiredMessage: string) =>
        Yup.string()
            .transform((value) => stripCommas(value))
            .required(requiredMessage)
            .concat(slabFieldTest("valid-decimal-format", "Please enter a valid number", isValidDecimalFormat))

    const getSlabRange = (slab: { slab_min?: string; slab_max?: string }) => {
        const slab_min = Number(`${slab?.slab_min ?? ""}`.replaceAll(',', ''))
        const slab_max = Number(`${slab?.slab_max ?? ""}`.replaceAll(',', ''))

        if (Number.isNaN(slab_min) || Number.isNaN(slab_max)) {
            return null
        }

        return { slab_min, slab_max }
    }

    const doSlabRangesOverlap = (minA: number, maxA: number, minB: number, maxB: number) =>
        minA <= maxB && minB <= maxA

    // Schema: validate only the active fee-type table; slab rows skip validation when freezed
    return Yup.object().shape({
        feeType: Yup.string(),
        fixedFeeTableData: Yup.array().when("feeType", {
            is: FEE_TYPE_VALUES.FIXED_FEE,
            then: () => Yup.array().of(
                Yup.object().shape({
                    invoiceStatus: Yup.string(),
                    fixed_rate: fixedFeeAmountValidator,
                })
            ),
            otherwise: () => Yup.array(),
        }),
        auaFeeVariableTableData: Yup.array().when("feeType", {
            is: FEE_TYPE_VALUES.AUA_FEE_VARIABLE,
            then: () => Yup.array().of(
                Yup.object().shape({
                    invoiceStatus: Yup.string(),
                    variable_rate: feePercentValidator,
                })
            ),
            otherwise: () => Yup.array(),
        }),
        auaFeeSlabWiseTableData: Yup.array().when("feeType", {
            is: FEE_TYPE_VALUES.AUA_FEE_SLAB_WISE,
            then: () => Yup.array().of(
                Yup.object().shape({
                    invoiceStatus: Yup.string(),
                    slabs: Yup.array().when("invoiceStatus", {
                        is: (invoiceStatus: string) => isFreezedStatus(invoiceStatus),
                        then: () => Yup.array(),
                        otherwise: () => Yup.array().of(
                            Yup.object().shape({
                                rate: slabFeePercentValidator,
                                // slab_min: slabAumValidator("Min Aum is required"),
                                slab_max: slabAumValidator("Max Aum is required")
                                    // Per-slab: max must exceed min (deferred until both are valid numbers)
                                    .test(
                                        "max-greater-than-min",
                                        "Max Aum should be greater than Min Aum",
                                        function (value) {
                                            const slab_min = getTrimmedValue(`${this.parent?.slab_min ?? ""}`.replaceAll(',', ''))

                                            // Checking if further validation of slab_max is required
                                            if (!shouldValidateSlabField(value ?? "")
                                                // || !shouldValidateSlabField(slab_min)
                                            ) {
                                                return true
                                            }

                                            // Checking if further validation of format of slab_max is required
                                            if (!isValidDecimalFormat(getTrimmedValue(value ?? ""))
                                                // || !isValidDecimalFormat(slab_min)
                                            ) {
                                                return true
                                            }

                                            return Number(`${value ?? ""}`.replaceAll(',', '')) > Number(`${slab_min}`.replaceAll(',', ''))
                                        }
                                    ),
                            }))
                            // Row-level: compare every slab pair; attach overlap error to the later slab's slab_max
                            .test("no-overlapping-slabs", function (slabs) {
                                const slabsData = slabs ?? []

                                for (let earlierIndex = 0; earlierIndex < slabsData.length; earlierIndex++) {
                                    const earlierRange = getSlabRange(slabsData[earlierIndex])

                                    if (!earlierRange) {
                                        continue
                                    }

                                    for (let laterIndex = earlierIndex + 1; laterIndex < slabsData.length; laterIndex++) {
                                        const laterRange = getSlabRange(slabsData[laterIndex])

                                        if (!laterRange) {
                                            continue
                                        }

                                        if (doSlabRangesOverlap(
                                            earlierRange.slab_min,
                                            earlierRange.slab_max,
                                            laterRange.slab_min,
                                            laterRange.slab_max
                                        )) {
                                            return this.createError({
                                                path: `${this.path}[${laterIndex}].slab_max`,
                                                message: `Overlaps with Slab ${earlierIndex + 1}`,
                                            })
                                        }
                                    }
                                }
                                return true
                            }),
                    }),
                })
            ),
            otherwise: () => Yup.array(),
        }),
    })
}

export interface FixedFeeTableDataObj extends Omit<FamilyMembersFeeListDataObj, 'fixed_rate' | 'variable_rate' | 'slabs'> {
    fixed_rate: string;
    old_fixed_rate: number | null;
    old_variable_rate: number | null;
    old_slabs: Array<SlabDataObj> | null;
}

export interface AuaFeeVariableTableDataObj extends Omit<FamilyMembersFeeListDataObj, 'fixed_rate' | 'variable_rate' | 'slabs'> {
    variable_rate: string;
    old_fixed_rate: number | null;
    old_variable_rate: number | null;
    old_slabs: Array<SlabDataObj> | null;
}

export interface SlabsObj {
    slab_min: string;
    slab_max: string;
    rate: string;
}

export interface AuaFeeSlabWiseTableDataObj extends Omit<FamilyMembersFeeListDataObj, 'fixed_rate' | 'variable_rate' | 'slabs'> {
    slabs: Array<SlabsObj>;
    old_fixed_rate: number | null;
    old_variable_rate: number | null;
    old_slabs: Array<SlabDataObj> | null;
}

export type FeeTypeTableDataMap = {
    [FEE_TYPE_VALUES.FIXED_FEE]: Array<FixedFeeTableDataObj>;
    [FEE_TYPE_VALUES.AUA_FEE_VARIABLE]: Array<AuaFeeVariableTableDataObj>;
    [FEE_TYPE_VALUES.AUA_FEE_SLAB_WISE]: Array<AuaFeeSlabWiseTableDataObj>;
}

export interface TableFormValues {
    familyId: string;
    familyName: string;
    hofName: string;
    hofClientCode: string;
    feeType: string;
    fixedFeeTableData: Array<FixedFeeTableDataObj>;
    auaFeeVariableTableData: Array<AuaFeeVariableTableDataObj>;
    auaFeeSlabWiseTableData: Array<AuaFeeSlabWiseTableDataObj>;
    isFeeTypeDisabled: boolean;
    isSubmitDisabled: boolean;
}

const initialTableFormValues: TableFormValues = {
    familyId: "",
    familyName: "",
    hofName: "",
    hofClientCode: "",
    feeType: "",
    fixedFeeTableData: [],
    auaFeeVariableTableData: [],
    auaFeeSlabWiseTableData: [],
    isFeeTypeDisabled: false,
    isSubmitDisabled: false
}

export default function ModifyFeeSetup(): JSX.Element {
    const theme = useTheme()
    const dispatch = useDispatch()

    const history = useHistory();
    const { pathname: pathName } = history.location
    const initialRouter = pathName.split('/').filter((item) => item !== '')

    const params: Record<string, string> = useParams()

    // Data from Redux
    const { permissions = [] } = useSelector((store: RootStateType) => store.auth);
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    // State
    const [loadingStatus, setLoadingStatus] = useState<"appliedFilterLoading" | "formSubmitLoading" | "">("")
    const [areFiltersApplied, setAreFiltersApplied] = useState<boolean>(false);
    const [tableFormInitialValues, setTableFormInitialValues] = useState<TableFormValues>(initialTableFormValues)
    const [feeSetupStatusPopupData, setFeeSetupStatusPopupData] = useState<UpdateRiaFeeResponseBody & { isOpen: boolean }>({
        isOpen: false,
        isSuccess: false,
        message: "",
        body: ""
    })

    const routerPath = (isModuleAllowed({ value: "MFDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [
        {
            name: 'Dashboard',
            path: '/dashboard',
            textSx: { paddingLeft: "0px" }
        },
        {
            name: 'RIA Fee Setup',
            path: `/${initialRouter[0]}`,
            isTextLowerCase: false
        },
        {
            name: 'Modify',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`,
            isTextLowerCase: false
        }
    ] : [])

    const filterFormikRef = useRef<FormikProps<FilterValues>>(null);
    const tableFormFormikRef = useRef<FormikProps<TableFormValues>>(null);
    const lastAppliedFilterValuesRef = useRef<FilterValues | null>(null);

    const handleYearChange = (event: SelectChangeEvent<unknown>) => {
        if (!filterFormikRef?.current) {
            return
        }

        const { values, setValues } = filterFormikRef.current

        setValues({
            ...values,
            year: event.target.value as string,
            quarter: ""
        })
    }

    const handleApplyFilters = async (values: FilterValues) => {
        try {
            setLoadingStatus("appliedFilterLoading")

            const payload = {
                year: Number(values.year),
                quarter: values.quarter,
                familyId: params?.familyId ?? ""
            }

            const response = await (dispatch(getFamilyMembersFeeListData(payload)) as unknown as Promise<GetFamilyMembersFeeListDataResponseBody>)

            if (!response) {
                setAreFiltersApplied(false)
                setTableFormInitialValues(initialTableFormValues)
            }

            if (!response?.isSuccess) {
                dispatch(showError(response?.message || "Data not found"))

                setAreFiltersApplied(false)
                setTableFormInitialValues(initialTableFormValues)
            }

            if (response.isSuccess) {

                const modifiedFixedFeeResponse = (response?.data?.members ?? []).map((obj: FamilyMembersFeeListDataObj) => ({
                    clientCode: obj?.clientCode ?? "",
                    name: obj?.name ?? "",
                    isHof: obj?.isHof ?? "",
                    investorType: obj?.investorType ?? "",
                    invoiceStatus: obj?.invoiceStatus ?? "",
                    status: obj?.status ?? "",
                    fixed_rate: (
                        (obj?.fixed_rate !== undefined) &&
                        (obj?.fixed_rate !== null)
                    ) ? Number(obj?.fixed_rate ?? "")?.toLocaleString(locale) : "",
                    old_fixed_rate: obj.fixed_rate,
                    old_variable_rate: obj.variable_rate,
                    old_slabs: obj.slabs,
                    contactNo: obj?.contactNo ?? "",
                    email: obj?.email ?? ""
                }))

                const modifiedAuaFeeVariableResponse = (response?.data?.members ?? []).map((obj: FamilyMembersFeeListDataObj) => ({
                    clientCode: obj?.clientCode ?? "",
                    name: obj?.name ?? "",
                    isHof: obj?.isHof ?? "",
                    investorType: obj?.investorType ?? "",
                    invoiceStatus: obj?.invoiceStatus ?? "",
                    status: obj?.status ?? "",
                    variable_rate: `${obj?.variable_rate ?? ""}`,
                    old_fixed_rate: obj.fixed_rate,
                    old_variable_rate: obj.variable_rate,
                    old_slabs: obj.slabs,
                    contactNo: obj?.contactNo ?? "",
                    email: obj?.email ?? ""
                }))

                const modifiedAuaFeeSlabWiseResponse = (response?.data?.members ?? []).map((obj: FamilyMembersFeeListDataObj) => ({
                    clientCode: obj?.clientCode ?? "",
                    name: obj?.name ?? "",
                    isHof: obj?.isHof ?? "",
                    investorType: obj?.investorType ?? "",
                    invoiceStatus: obj?.invoiceStatus ?? "",
                    status: obj?.status ?? "",
                    slabs: ((obj?.slabs ?? []).length) ? ((obj?.slabs ?? []).map(slabData => ({
                        slab_min: (
                            (slabData?.slab_min !== undefined) &&
                            (slabData?.slab_min !== null)
                        ) ? Number(slabData?.slab_min ?? "")?.toLocaleString(locale) : "",
                        slab_max: (
                            (slabData?.slab_max !== undefined) &&
                            (slabData?.slab_max !== null)
                        ) ? Number(slabData?.slab_max ?? "")?.toLocaleString(locale) : "",
                        rate: `${slabData?.rate ?? ""}`
                    }))) : [{
                        slab_min: "0",
                        slab_max: "",
                        rate: ""
                    }],
                    old_fixed_rate: obj.fixed_rate,
                    old_variable_rate: obj.variable_rate,
                    old_slabs: obj.slabs,
                    contactNo: obj?.contactNo ?? "",
                    email: obj?.email ?? ""
                }))

                const isFeeTypeDisabled = (response?.data?.members ?? []).some((obj: FamilyMembersFeeListDataObj) => ["freezed", "freeze"].includes(`${obj?.invoiceStatus ?? ""}`.toLowerCase()))

                const isSubmitDisabled = (response?.data?.members ?? []).every((obj: FamilyMembersFeeListDataObj) => ["freezed", "freeze"].includes(`${obj?.invoiceStatus ?? ""}`.toLowerCase()))

                const newTableFormValues: TableFormValues = {
                    familyId: response?.data?.familyId ?? "",
                    familyName: response?.data?.familyName ?? "",
                    hofName: response?.data?.hofName ?? "",
                    hofClientCode: response?.data?.hofClientCode ?? "",
                    feeType: response?.data?.feeType ?? FEE_TYPE_VALUES.FIXED_FEE,
                    fixedFeeTableData: modifiedFixedFeeResponse,
                    auaFeeVariableTableData: modifiedAuaFeeVariableResponse,
                    auaFeeSlabWiseTableData: modifiedAuaFeeSlabWiseResponse,
                    isFeeTypeDisabled,
                    isSubmitDisabled
                }

                setTableFormInitialValues(newTableFormValues)

                if (tableFormFormikRef.current) {
                    tableFormFormikRef.current.resetForm({ values: newTableFormValues })
                }

                lastAppliedFilterValuesRef.current = { ...values }

                setAreFiltersApplied(true)
            }
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setLoadingStatus("")
        }
    };

    const handleFeeTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!tableFormFormikRef?.current) {
            return
        }

        const { values, setValues } = tableFormFormikRef.current

        setValues({
            ...values,
            feeType: event.target.value as string,
            fixedFeeTableData: tableFormInitialValues?.fixedFeeTableData ?? [],
            auaFeeVariableTableData: tableFormInitialValues?.auaFeeVariableTableData ?? [],
            auaFeeSlabWiseTableData: tableFormInitialValues?.auaFeeSlabWiseTableData ?? []
        }, false)
    }

    const handleTableFormSubmit = async (values: TableFormValues) => {
        try {
            if (!lastAppliedFilterValuesRef?.current) {
                return
            }

            setLoadingStatus("formSubmitLoading")

            const selectedYear = Number(lastAppliedFilterValuesRef.current?.year ?? "")
            const selectedQuarter = lastAppliedFilterValuesRef.current?.quarter ?? ""

            const oldFeeType = tableFormInitialValues.feeType
            const newFeeType = values.feeType
            const isFeeTypeChanged = (oldFeeType !== newFeeType)

            // Below function is invoked when oldFeeType and newFeeType are same i.e., FIXED, and we want to check whether value is changed for a particular member in that feetype
            const isFixedTypeMemberModified = (
                currentData: FixedFeeTableDataObj,
                initialData: FixedFeeTableDataObj
            ): "1" | "0" => {
                if (["freezed", "freeze"].includes(`${currentData?.invoiceStatus ?? ""}`.toLowerCase())) {
                    return "0"
                }

                const currentFixedRateValue = `${currentData.fixed_rate ?? ""}`.replaceAll(',', '').trim()
                const initialFixedRateValue = `${initialData.fixed_rate ?? ""}`.replaceAll(',', '').trim()

                return (currentFixedRateValue !== initialFixedRateValue) ? "1" : "0"
            }

            // Below function is invoked when oldFeeType and newFeeType are same i.e., VARIABLE, and we want to check whether value is changed for a particular member in that feetype
            const isVariableTypeMemberModified = (
                currentData: AuaFeeVariableTableDataObj,
                initialData: AuaFeeVariableTableDataObj
            ): "1" | "0" => {
                if (["freezed", "freeze"].includes(`${currentData?.invoiceStatus ?? ""}`.toLowerCase())) {
                    return "0"
                }

                const currentVariableRateValue = `${currentData.variable_rate ?? ""}`.trim()
                const initialVariableRateValue = `${initialData.variable_rate ?? ""}`.trim()

                return (currentVariableRateValue !== initialVariableRateValue) ? "1" : "0"
            }

            // Below function is invoked when oldFeeType and newFeeType are same i.e., SLAB, and we want to check whether value is changed for a particular member in that feetype
            const isSlabTypeMemberModified = (
                currentData: AuaFeeSlabWiseTableDataObj,
                initialData: AuaFeeSlabWiseTableDataObj
            ): "1" | "0" => {
                if (["freezed", "freeze"].includes(`${currentData?.invoiceStatus ?? ""}`.toLowerCase())) {
                    return "0"
                }

                const currentSlabs = currentData.slabs ?? []
                const initialSlabs = initialData.slabs ?? []

                // Different slab count => slabs were added or removed
                if (currentSlabs.length !== initialSlabs.length) {
                    return "1"
                }

                // Compare each slab field-by-field in order
                for (let slabIndex = 0; slabIndex < currentSlabs.length; slabIndex++) {
                    const curr = currentSlabs[slabIndex]
                    const init = initialSlabs[slabIndex]

                    const isSlabMinChanged = (`${curr?.slab_min ?? ""}`.replaceAll(',', '').trim() !== `${init?.slab_min ?? ""}`.replaceAll(',', '').trim())
                    const isSlabMaxChanged = (`${curr?.slab_max ?? ""}`.replaceAll(',', '').trim() !== `${init?.slab_max ?? ""}`.replaceAll(',', '').trim())
                    const isRateChanged = (`${curr?.rate ?? ""}`.trim() !== `${init?.rate ?? ""}`.trim())

                    if (isSlabMinChanged || isSlabMaxChanged || isRateChanged) {
                        return "1"
                    }
                }

                return "0"
            }

            const buildMemberList = () => {
                const mapCommonFields = (member: FixedFeeTableDataObj | AuaFeeVariableTableDataObj | AuaFeeSlabWiseTableDataObj) => ({
                    portfolio: member.clientCode ?? "",
                    clientCode: member.clientCode ?? "",
                    name: member.name ?? "",
                    investorType: member.investorType ?? "",
                    invoiceStatus: member.invoiceStatus ?? "",
                    contactNo: member.contactNo ?? "",
                    email: member.email ?? "",
                    status: member.status ?? ""
                })

                switch (newFeeType) {
                    case FEE_TYPE_VALUES.FIXED_FEE: {
                        const activeData = values.fixedFeeTableData
                        const initialData = tableFormInitialValues.fixedFeeTableData

                        return activeData.map((member, index) => {
                            const isModified: "1" | "0" = isFeeTypeChanged
                                ? "1"
                                : isFixedTypeMemberModified(member, initialData[index])

                            return {
                                ...mapCommonFields(member),
                                fixed_rate: (member.fixed_rate !== "") ? Number(`${member?.fixed_rate ?? ""}`.replaceAll(',', '')) : null,
                                variable_rate: null,
                                slabs: null,
                                isModified
                            }
                        })
                    }
                    case FEE_TYPE_VALUES.AUA_FEE_VARIABLE: {
                        const activeData = values.auaFeeVariableTableData
                        const initialData = tableFormInitialValues.auaFeeVariableTableData

                        return activeData.map((member, index) => {
                            const isModified: "1" | "0" = isFeeTypeChanged
                                ? "1"
                                : isVariableTypeMemberModified(member, initialData[index])

                            return {
                                ...mapCommonFields(member),
                                fixed_rate: null,
                                variable_rate: (member.variable_rate !== "") ? Number(member.variable_rate) : null,
                                slabs: null,
                                isModified
                            }
                        })
                    }
                    case FEE_TYPE_VALUES.AUA_FEE_SLAB_WISE: {
                        const activeData = values.auaFeeSlabWiseTableData
                        const initialData = tableFormInitialValues.auaFeeSlabWiseTableData

                        return activeData.map((member, index) => {
                            const isModified: "1" | "0" = isFeeTypeChanged
                                ? "1"
                                : isSlabTypeMemberModified(member, initialData[index])

                            return {
                                ...mapCommonFields(member),
                                fixed_rate: null,
                                variable_rate: null,
                                slabs: (member.slabs ?? []).length ? (member.slabs ?? []).map((slab) => ({
                                    slab_min: Number(`${slab?.slab_min ?? ""}`.replaceAll(',', '')),
                                    slab_max: Number(`${slab?.slab_max ?? ""}`.replaceAll(',', '')),
                                    rate: Number(slab.rate)
                                })) : null,
                                isModified
                            }
                        })
                    }
                    default:
                        return []
                }
            }

            const buildOldMemberList = () => {
                switch (oldFeeType) {
                    case FEE_TYPE_VALUES.FIXED_FEE: {
                        return tableFormInitialValues.fixedFeeTableData.map((member) => ({
                            clientCode: member.clientCode ?? "",
                            name: member.name ?? "",
                            investorType: member.investorType ?? "",
                            invoiceStatus: member.invoiceStatus ?? "",
                            fixed_rate: member.old_fixed_rate,
                            variable_rate: member.old_variable_rate,
                            slabs: member.old_slabs,
                            contactNo: member.contactNo ?? "",
                            email: member.email ?? "",
                            status: member.status ?? ""
                        }))
                    }
                    case FEE_TYPE_VALUES.AUA_FEE_VARIABLE: {
                        return tableFormInitialValues.auaFeeVariableTableData.map((member) => ({
                            clientCode: member.clientCode ?? "",
                            name: member.name ?? "",
                            investorType: member.investorType ?? "",
                            invoiceStatus: member.invoiceStatus ?? "",
                            fixed_rate: member.old_fixed_rate,
                            variable_rate: member.old_variable_rate,
                            slabs: member.old_slabs,
                            contactNo: member.contactNo ?? "",
                            email: member.email ?? "",
                            status: member.status ?? ""
                        }))
                    }
                    case FEE_TYPE_VALUES.AUA_FEE_SLAB_WISE: {
                        return tableFormInitialValues.auaFeeSlabWiseTableData.map((member) => ({
                            clientCode: member.clientCode ?? "",
                            name: member.name ?? "",
                            investorType: member.investorType ?? "",
                            invoiceStatus: member.invoiceStatus ?? "",
                            fixed_rate: member.old_fixed_rate,
                            variable_rate: member.old_variable_rate,
                            slabs: member.old_slabs,
                            contactNo: member.contactNo ?? "",
                            email: member.email ?? "",
                            status: member.status ?? ""
                        }))
                    }
                    default:
                        return []
                }
            }

            const payload = {
                year: selectedYear,
                quarter: selectedQuarter,
                familyId: values.familyId,
                newFeeType,
                oldFeeType,
                hofName: tableFormInitialValues.hofName,
                hofClientCode: tableFormInitialValues.hofClientCode,
                familyName: values.familyName,
                memberList: buildMemberList(),
                oldMemberList: buildOldMemberList()
            }

            const response = await (dispatch(updateRiaFee(payload)) as unknown as Promise<UpdateRiaFeeResponseBody>)

            if (response) {
                setFeeSetupStatusPopupData({
                    isOpen: true,
                    isSuccess: response?.isSuccess,
                    message: response?.message ?? "",
                    body: response?.body ?? ""
                })
            }
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setLoadingStatus("")
        }
    }

    const handleFeeSetupStatusPopupClose = (action: string) => {
        setFeeSetupStatusPopupData((prev) => ({
            ...prev,
            isOpen: false,
        }))

        if (action === "navigate") {
            history.replace(`/${initialRouter[0]}/history`);
        }
    }

    return (
        <>
            <Box sx={{ marginBottom: "1rem" }}>
                <BreadCrum array={routerPath} />
            </Box>
            <Typography
                variant="h1"
                sx={{
                    color: theme.palette.text.primary,
                    marginBottom: "2rem"
                }}
            >
                Modify RIA Fee Setup
            </Typography>
            <Box
                sx={{
                    padding: '25px',
                    borderRadius: '15px',
                    background: theme.palette.common.white,
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                    marginBottom: "2rem"
                }}
            >
                <Typography
                    variant='h2'
                    sx={{
                        color: theme.palette.text.primary,
                        marginBottom: "1rem"
                    }}
                >
                    Effective time period
                </Typography>
                <Formik
                    innerRef={filterFormikRef}
                    validationSchema={filterValidationSchema}
                    initialValues={initialFilterValues}
                    onSubmit={handleApplyFilters}
                >
                    {({ values }) => {
                        const quarterOptions = getQuartersForFiscalYear(values?.year ?? "")

                        return (
                            <Form>
                                <Grid
                                    container
                                    spacing={2}
                                    alignItems="flex-start"
                                    justifyContent="flex-start"
                                >
                                    <Grid
                                        item
                                        xs={12}
                                        sm={4}
                                        lg={3}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start'
                                        }}
                                    >
                                        <MFSelectField
                                            name='year'
                                            label="Fiscal Year *"
                                            items={YEARS}
                                            onChange={handleYearChange}
                                            disabled={["appliedFilterLoading", "formSubmitLoading"].includes(loadingStatus)}
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        sm={4}
                                        lg={3}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start'
                                        }}
                                    >
                                        <MFSelectField
                                            name='quarter'
                                            label="Quarter *"
                                            items={quarterOptions}
                                            disabled={["appliedFilterLoading", "formSubmitLoading"].includes(loadingStatus)}
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        xs={6}
                                        sm={3}
                                        md={2}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            justifyContent: 'flex-start',
                                            mt: { xs: 1, sm: 1, md: 2 },
                                        }}
                                    >
                                        <MFSubmitButton
                                            label="Apply Filters"
                                            loadingEnable={["appliedFilterLoading"].includes(loadingStatus)}
                                            loadingPosition="center"
                                            sx={{
                                                ...theme.typography.body_500,
                                                fontWeight: 600,
                                                padding: '15px',
                                                lineHeight: 1.5,
                                                minHeight: '52px',
                                                whiteSpace: 'nowrap',
                                                "& .MuiCircularProgress-root": {
                                                    color: theme.palette.text.primary,
                                                },
                                            }}
                                            disabled={["formSubmitLoading"].includes(loadingStatus)}
                                        />
                                    </Grid>
                                </Grid>
                            </Form>
                        )
                    }}
                </Formik>
            </Box >
            <Box
                sx={{
                    padding: '25px',
                    borderRadius: '15px',
                    background: theme.palette.common.white,
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                }}
            >
                {!areFiltersApplied && <NoData />}
                {areFiltersApplied && (
                    <Formik
                        innerRef={tableFormFormikRef}
                        validationSchema={tableFormValidationSchema(locale, currencyCode)}
                        initialValues={tableFormInitialValues}
                        validateOnBlur
                        validateOnChange={false}
                        enableReinitialize
                        onSubmit={handleTableFormSubmit}
                    >
                        {({ values }) => {
                            return (
                                <Form>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            rowGap: "6px",
                                            marginBottom: "2rem"
                                        }}
                                    >
                                        <Typography
                                            variant='h2'
                                            sx={{ color: theme.palette.text.primary }}
                                        >
                                            Choose Fee Type
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                columnGap: "5px",
                                                marginLeft: '-2px',
                                                mt: 0.5
                                            }}>
                                            <InfoIconOutlined />
                                            <Typography variant='subtitle_600' sx={{ color: theme.palette.grey[500] }}>
                                                Changing Fee Type will change it for every family member.
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {["appliedFilterLoading"].includes(loadingStatus) ? (
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "15px", mb: "3rem" }}>
                                            {FEE_TYPES.map((item) => (
                                                <Skeleton
                                                    key={item.value}
                                                    variant="rounded"
                                                    width={150}
                                                    height={36}
                                                    sx={{ borderRadius: "10px" }}
                                                />
                                            ))}
                                        </Box>
                                    ) : (
                                        <RadioGroup
                                            row
                                            value={values.feeType}
                                            onChange={handleFeeTypeChange}
                                            sx={{ flexWrap: "wrap", gap: "15px", mb: "3rem" }}
                                        >
                                            {FEE_TYPES.map((item) => {
                                                const isChecked = values.feeType === item.value

                                                return (
                                                    <FormControlLabel
                                                        key={item.value}
                                                        value={item.value}
                                                        label={item.label}
                                                        control={
                                                            <Radio
                                                                disableRipple
                                                                size="small"
                                                                sx={{
                                                                    padding: "5px",
                                                                    color: theme.palette.text.secondary,
                                                                    '&.Mui-checked': {
                                                                        color: theme.palette.primary[500],
                                                                    },
                                                                    ':hover': { background: 'none' },
                                                                    "&.Mui-disabled": {
                                                                        color: `${theme.palette.text.secondary}B3`,
                                                                        opacity: 0.5
                                                                    }
                                                                }}
                                                            />
                                                        }
                                                        sx={{
                                                            padding: "4px 15px 4px 8px",
                                                            borderRadius: "10px",
                                                            marginLeft: 0,
                                                            marginRight: 0,
                                                            columnGap: "8px",
                                                            backgroundColor: isChecked
                                                                ? `${theme.palette.primary[500]}1A`
                                                                : theme.palette.common.white,
                                                            border: isChecked
                                                                ? `1px solid ${theme.palette.primary[500]}`
                                                                : `0.5px solid ${theme.palette.text.secondary}80`,
                                                            '.MuiFormControlLabel-label': {
                                                                ...theme.typography.body_500,
                                                                color: isChecked
                                                                    ? theme.palette.primary[500]
                                                                    : theme.palette.text.secondary,
                                                                cursor: 'pointer',
                                                                "&.Mui-disabled": {
                                                                    pointerEvents: "auto",
                                                                    cursor: "not-allowed",
                                                                    color: `${theme.palette.text.secondary}B3`
                                                                }
                                                            },
                                                        }}
                                                        disabled={values.isFeeTypeDisabled || ["formSubmitLoading"].includes(loadingStatus)}
                                                    />
                                                )
                                            })}
                                        </RadioGroup>
                                    )}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            flexWrap: "wrap",
                                            gap: "16px",
                                            mb: "3rem"
                                        }}
                                    >
                                        <Typography
                                            variant='h2'
                                            sx={{ color: theme.palette.text.primary }}
                                        >
                                            Family Members Details
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                backgroundColor: theme.palette.primary[800],
                                                borderRadius: "8px",
                                                padding: "6px 14px",
                                                columnGap: "12px",
                                                maxWidth: "400px",
                                            }}
                                        >
                                            <Typography
                                                variant='subtitle_500'
                                                sx={{
                                                    color: theme.palette.text.primary,
                                                    minWidth: 0,
                                                    flex: "1 1 auto",
                                                    wordBreak: "break-word",
                                                    overflowWrap: "break-word",
                                                }}
                                            >
                                                {tableFormInitialValues?.familyName ?? "-"}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    width: "1px",
                                                    alignSelf: "stretch",
                                                    backgroundColor: `${theme.palette.text.primary}33`,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Typography
                                                variant='subtitle_500'
                                                sx={{
                                                    color: theme.palette.text.primary,
                                                    whiteSpace: "nowrap",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {tableFormInitialValues?.familyId ?? "-"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <FamilyMemberDetailsTable
                                        isLoading={["appliedFilterLoading"].includes(loadingStatus)}
                                        isDisabled={["formSubmitLoading"].includes(loadingStatus)}
                                    />
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            marginTop: "3rem",
                                        }}
                                    >
                                        <Box sx={{ width: { xs: "100%", sm: "200px" } }}>
                                            <MFSubmitButton
                                                label="Save Details"
                                                loadingEnable={["formSubmitLoading"].includes(loadingStatus)}
                                                loadingPosition="center"
                                                sx={(theme: Theme) => ({
                                                    ...theme.typography.body_500,
                                                    fontWeight: 600,
                                                    padding: '12px',
                                                    lineHeight: 1.5,
                                                    "& .MuiCircularProgress-root": {
                                                        color: theme.palette.text.primary,
                                                    },
                                                })}
                                                disabled={values.isSubmitDisabled || ["appliedFilterLoading"].includes(loadingStatus)}
                                            />
                                        </Box>
                                    </Box>
                                </Form>
                            )
                        }}
                    </Formik>
                )}
            </Box>
            <StatusPopup
                open={feeSetupStatusPopupData.isOpen}
                isSuccess={feeSetupStatusPopupData.isSuccess}
                message={feeSetupStatusPopupData.message}
                buttonText={feeSetupStatusPopupData.isSuccess ? "Done" : "Cancel"}
                onButtonClick={handleFeeSetupStatusPopupClose}
            />
        </>
    )
}